import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Route, Switch} from "react-router-dom";
import {NotificationManager} from 'react-notifications';
import styled from "styled-components";
import {ScalePrintTabs} from "./ScalePrintTabs";
import {useApi} from "../../lib/api";
import useAsync from "../../lib/use-async";
import ListScreen from "../../components/ListScreen";
import ListItem from "../../components/ListItem";
import Loading from "../../components/Loading";
import Error from "../../components/Error";
import Button from "../../components/Button";
import Input from "../../components/Input";
import TimeAgo from "../../components/TimeAgo";
import {Tag} from "../../components/TagComponents";
import {useUrlState} from "../../lib/with-url-state";
import search from "../../lib/search";
import {formSort} from "../../lib/sort";
import {T} from "../../components/I18n";
import AttachTemplatesModal from "./AttachTemplatesModal";
import PrintLayoutFormScreen from "./PrintLayoutFormScreen";

export function PrintLayoutsTab() {
  // contains the routes for each different part of label layouts
  return (
    <Switch>
      <Route path="/devices/print-layouts/create" exact component={PrintLayoutFormScreen} />
      <Route path="/devices/print-layouts/:id" exact component={PrintLayoutFormScreen} />
      <Route path="/devices/print-layouts" exact component={PrintLayoutsList} />
    </Switch>
  );
}

function PrintLayoutsList() {
  const api = useApi();
  const [{filter, sort, attachment}, setUrlState] = useUrlState({filter: '', sort: 'asc', attachment: 'all'});
  const [getLabelLayouts, {loading: layoutsLoading, error: layoutsError, result: layoutsResult}] = useAsync(api.getLabelLayouts);
  const [getCompanyTemplates, {loading: templatesLoading, result: templatesResult}] = useAsync(api.getCompanyTemplates);
  const [labelLayouts, setLabelLayouts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [attachToLayout, setAttachToLayout] = useState(null);

  useEffect(() => {
    getLabelLayouts();
    getCompanyTemplates();
  }, []);

  useEffect(() => {
    setLabelLayouts(layoutsResult || []);
  }, [layoutsResult]);

  useEffect(() => {
    setTemplates(templatesResult || []);
  }, [templatesResult]);

  const handleSearch = useCallback((e) => {
    setUrlState({filter: e.target.value});
  }, [setUrlState]);

  const handleSortChange = useCallback((e) => {
    setUrlState({sort: e.target.value});
  }, [setUrlState]);

  const handleAttachmentFilterChange = useCallback((e) => {
    setUrlState({attachment: e.target.value});
  }, [setUrlState]);

  const handleAttachClose = useCallback(() => {
    setAttachToLayout(null);
  }, []);

  const handleAttachSaved = useCallback(() => {
    setAttachToLayout(null);
    getCompanyTemplates();
  }, [getCompanyTemplates]);

  const templatesByLayoutId = useMemo(() => {
    const map = {};
    for (const t of templates) {
      if (!t.labelLayoutId) continue;
      if (!map[t.labelLayoutId]) map[t.labelLayoutId] = [];
      map[t.labelLayoutId].push(t);
    }
    return map;
  }, [templates]);

  const handleDelete = useCallback(async (layout) => {
    const attached = templatesByLayoutId[layout.id] || [];
    let message = `Are you sure you want to delete the print layout "${layout.name}"?`;
    if (attached.length > 0) {
      const names = attached.map(t => `"${t.name}"`).join(', ');
      message += `\n\nThis layout is attached to the following ${attached.length === 1 ? 'template' : 'templates'}: ${names}.`
        + `\n\nScale printing for ${attached.length === 1 ? 'this template' : 'these templates'} will fail until a new print layout is attached.`;
    }

    if (!window.confirm(message)) {
      return;
    }

    try {
      await api.deleteLabelLayout(layout.id);
      setLabelLayouts(prev => prev.filter(l => l.id !== layout.id));
      NotificationManager.success(<><T noTranslate>{layout.name}</T> was deleted.</>);
    }
    catch (e) {
      NotificationManager.error('Failed to delete print layout. Please try again.');
    }
  }, [api, templatesByLayoutId]);

  const filteredAndSortedLayouts = labelLayouts
    .filter(layout => search(layout.name, filter))
    .filter(layout => {
      if (attachment === 'all' || templatesLoading) return true;
      const attached = (templatesByLayoutId[layout.id] || []).length > 0;
      return attachment === 'attached' ? attached : !attached;
    })
    .sort(formSort(
      sort,
      layout => layout.name,
      layout => ({
        created: layout.createdDate,
        lastModified: layout.updatedDate || layout.createdDate
      })
    ));

  return (
    <>
      <ListScreen
        title="Devices"
        activeLink="Devices"
        tabs={<ScalePrintTabs />}
        placeholder="Search print layouts..."
        search={filter}
        onSearch={handleSearch}
        loading={layoutsLoading && <Loading fullScreen />}
        error={layoutsError && <Error fullScreen />}
        primaryAction={<Button to="/devices/print-layouts/create">Create Print Layout</Button>}
        items={filteredAndSortedLayouts}
        filter={filter}
        toolbarActions={[
          <Input id="layout-sort" label="Sort by:" type="select" value={sort} onChange={handleSortChange}>
            <option value="asc">Alphabetical</option>
            <option value="desc">Reverse Alphabetical</option>
            <option value="createdDateNewest">Created Date (newest first)</option>
            <option value="createdDateOldest">Created Date (oldest first)</option>
            <option value="lastModifiedDateNewest">Last Modified Date (newest first)</option>
            <option value="lastModifiedDateOldest">Last Modified Date (oldest first)</option>
          </Input>,
          <Input id="layout-attachment-filter" label="Filter:" type="select" value={attachment} onChange={handleAttachmentFilterChange}>
            <option value="all">All Layouts</option>
            <option value="attached">Attached to a Template</option>
            <option value="unattached">Not Attached</option>
          </Input>
        ]}
        empty={
          labelLayouts.length < 1 ? (
            <p>No print layouts have been created yet. Click <b>Create Print Layout</b> to get started.</p>
          ) : (
            <p>No print layouts found.</p>
          )
        }
      >
        {(layout, i) => {
          const attached = templatesByLayoutId[layout.id] || [];
          return (
            <ListItem
              first={i === 0}
              options={[
                {title: 'Edit', to: `/devices/print-layouts/${layout.id}`},
                {title: 'Attach Templates', onClick: () => setAttachToLayout(layout)},
                {title: 'Delete', onClick: () => handleDelete(layout)}
              ]}
            >
              <LayoutRow>
                <Name><T noTranslate>{layout.name}</T></Name>
                <Updated>
                  {layout.updatedDate ? (
                    <>Updated <TimeAgo date={layout.updatedDate} /></>
                  ) : (
                    <>Created <TimeAgo date={layout.createdDate} /></>
                  )}
                </Updated>
                <TemplateTags>
                  {templatesLoading ? (
                    <NoTemplates>Loading templates...</NoTemplates>
                  ) : attached.length > 0 ? (
                    attached.map(t => (
                      <TemplateTag key={t.id} title={t.name}>
                        <T noTranslate>{t.name}</T>
                      </TemplateTag>
                    ))
                  ) : (
                    <NoTemplates>No templates attached</NoTemplates>
                  )}
                </TemplateTags>
              </LayoutRow>
            </ListItem>
          );
        }}
      </ListScreen>
      <AttachTemplatesModal
        layout={attachToLayout}
        open={!!attachToLayout}
        templates={templates}
        templatesLoading={templatesLoading}
        onClose={handleAttachClose}
        onSaved={handleAttachSaved}
      />
    </>
  );
}

const LayoutRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Name = styled.strong`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Updated = styled.small`
  display: block;
  color: #747474;
`;

const TemplateTags = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;
  min-height: 1.75rem;
`;

const TemplateTag = styled(Tag)`
  cursor: default;

  &:hover {
    background-color: transparent;
    border-color: #999;
    color: #495057;
  }
`;

const NoTemplates = styled.small`
  color: #a0a0a0;
  font-style: italic;
`;